import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authConfig, confirmResetCode, defaultCollection, googleStartUrl, readInvitation, requestReset, resendCode, signIn, signUp, updateProfile, verifyCode } from '../../auth/passwordSession';
import { safeReturnPath } from '../../auth/session';
import { getConfig } from '../../config/runtimeConfig';
import { useWorkspaceStore } from '../../stores/workspaceStore';
import { IconEye, IconEyeOff, IconGoogle } from '../ui/icons';
import { Select } from '../ui/Select';
import { toast } from '../ui/toast';

/**
 * The two-pane auth screens (`auth.open`): a dark left pane with the brand
 * and a tiled backdrop, a 380px column on the right with one of sign in,
 * create account, reset (email), check your email (six digits), choose a new
 * password, tell us about you, or join a workspace (invitation).
 *
 * Flows, as the prototype has them:
 *   sign up → code → tell us about you → home
 *   forgot  → code → new password → home
 *   invite  → name + password → home (the invitation already proved the address)
 * The code screens sign the person in when the code is spent (the server
 * issues the session), so "tell us about you" saves to a real profile.
 */
const BASE = import.meta.env.BASE_URL.replace(/\/+$/, '');
// Where a website owns `/`, the logo goes to it and Terms and Privacy are its
// pages (RuntimeConfig.website) — opened beside the form, which keeps what was
// typed into it.
const home = (nav: (to: string) => void) => (getConfig().website ? window.location.assign('/') : nav('/explore'));
const legal = (path: string) => (getConfig().website ? { href: path, target: '_blank', rel: 'noopener' } : { href: '#' });
const TILES = ['#efc4d3', '#c67f9b', '#8a5375', '#dcb896', '#b07a4d', '#6e4426', '#e9c6e0', '#b982b8', '#6f4a7d', '#bcd4e6', '#7a9fc2', '#3f5f86', '#d7d7cf', '#9c9c92', '#4f4f48', '#c67f9b'];
const ENTRY: ReadonlySet<AuthScreen> = new Set<AuthScreen>(['signin', 'signup', 'forgot']);
const ROLE_OPTIONS = [
  { value: 'pathologist', label: 'Pathologist' },
  { value: 'resident', label: 'Resident / trainee' },
  { value: 'scientist', label: 'Scientist / researcher' },
  { value: 'other', label: 'Other' },
];
const ROLE_TITLE: Record<string, string> = { pathologist: 'Pathologist', resident: 'Resident', scientist: 'Scientist', other: '' };
const emailOk = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());

export type AuthScreen = 'signin' | 'signup' | 'forgot' | 'verify' | 'reset' | 'setup' | 'invite';

const input: React.CSSProperties = { height: 40, padding: '0 12px', border: '1px solid #e3e3e1', borderRadius: 8, background: '#fff', fontSize: 14, color: '#0f0f10', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' };
const label: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: '#3f3f44' };
const primary: React.CSSProperties = { height: 40, border: 0, borderRadius: 8, background: '#0f0f10', color: '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', width: '100%' };
const linkStyle: React.CSSProperties = { color: '#0f0f10', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: 2 };

function H({ title, blurb }: { title: string; blurb: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: '-.02em' }}>{title}</h1>
      <p style={{ margin: 0, fontSize: 14, color: '#6b6b70', lineHeight: 1.5 }}>{blurb}</p>
    </div>
  );
}
function L({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <a href="#" onClick={(e) => { e.preventDefault(); onClick(); }} style={linkStyle}>
      {children}
    </a>
  );
}
function Err({ children }: { children: string }) {
  return children ? <div style={{ fontSize: 13, color: '#b3261e', lineHeight: 1.4 }}>{children}</div> : null;
}
function Google({ signup, offered }: { signup: boolean; offered: boolean }) {
  if (!offered) return null;
  return (
    <>
      <button type="button" className="tr-hover-soft" onClick={() => window.location.assign(googleStartUrl(signup))} style={{ height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, border: '1px solid #e3e3e1', borderRadius: 8, background: '#fff', color: '#0f0f10', fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', width: '100%' }}>
        <IconGoogle size={16} />
        {signup ? 'Sign up with Google' : 'Continue with Google'}
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: '#8a8a8f', fontSize: 12.5 }}>
        <div style={{ flex: 1, height: 1, background: '#ebebea' }} />
        or
        <div style={{ flex: 1, height: 1, background: '#ebebea' }} />
      </div>
    </>
  );
}
function Password({ value, onChange, onEnter, autoComplete, autoFocus }: { value: string; onChange: (v: string) => void; onEnter: () => void; autoComplete: 'current-password' | 'new-password'; autoFocus?: boolean }) {
  const [shown, setShown] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input type={shown ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && onEnter()} autoFocus={autoFocus} autoComplete={autoComplete} style={{ ...input, paddingRight: 40 }} />
      <button type="button" title={shown ? 'Hide password' : 'Show password'} className="tr-hover-track-ink" onClick={() => setShown(!shown)} style={{ position: 'absolute', top: 6, right: 6, width: 28, height: 28, display: 'grid', placeItems: 'center', border: 0, borderRadius: 6, background: 'transparent', color: '#8a8a8f', cursor: 'pointer' }}>
        {shown ? <IconEyeOff size={15} /> : <IconEye size={15} />}
      </button>
    </div>
  );
}

/** The six inputs: one digit each, paste fills all, Backspace steps back. */
function Digits({ code, onChange, onEnter }: { code: string[]; onChange: (c: string[]) => void; onEnter: () => void }) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  return (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
      {code.map((v, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          value={v}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus={i === 0}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const d = (e.target.value.match(/\d/g) || []).pop() || '';
            const next = [...code];
            next[i] = d;
            onChange(next);
            if (d && i < 5) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !code[i] && i > 0) {
              const next = [...code];
              next[i - 1] = '';
              onChange(next);
              refs.current[i - 1]?.focus();
            }
            if (e.key === 'Enter') onEnter();
          }}
          onPaste={(e) => {
            const t = (e.clipboardData.getData('text').match(/\d/g) || []).slice(0, 6);
            if (!t.length) return;
            e.preventDefault();
            const next = ['', '', '', '', '', ''];
            t.forEach((d, k) => { next[k] = d; });
            onChange(next);
            refs.current[Math.min(t.length, 5)]?.focus();
          }}
          style={{ width: 52, height: 56, textAlign: 'center', fontSize: 22, fontWeight: 500, fontFamily: "'JetBrains Mono', monospace", border: '1px solid #e3e3e1', borderRadius: 8, background: '#fff', color: '#0f0f10', padding: 0, boxSizing: 'border-box', outline: 'none' }}
        />
      ))}
    </div>
  );
}

export function AuthPage({ screen: initial }: { screen: AuthScreen }) {
  const nav = useNavigate();
  const { pathname, search } = useLocation();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [screen, setScreen] = useState<AuthScreen>(initial);
  const [mode, setMode] = useState<'signup' | 'reset'>(initial === 'forgot' ? 'reset' : 'signup');
  const [google, setGoogle] = useState(false);
  const [signupOffered, setSignupOffered] = useState(true);
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [institution, setInstitution] = useState('');
  const [role, setRole] = useState('pathologist');
  const [error, setError] = useState(params.get('error') ?? '');
  const [busy, setBusy] = useState(false);
  const [invite, setInvite] = useState<{ email: string; workspace: string } | null>(null);
  const inviteToken = params.get('token') ?? '';
  useEffect(() => { setScreen(initial); setError(params.get('error') ?? ''); }, [initial, params]);
  useEffect(() => {
    authConfig().then((c) => { setGoogle(!!c.google); setSignupOffered(c.signup); }).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (initial !== 'invite' || !inviteToken) return;
    readInvitation(inviteToken).then((i) => { setInvite(i); setEmail(i.email); }).catch((e: Error) => setError(e.message));
  }, [initial, inviteToken]);

  // Sign in, Create account and Forgot are pages of their own (/signin,
  // /signup, /forgot): moving between them changes the URL, so each can be
  // linked to, reloaded and gone back from. The steps that follow (verify,
  // reset, setup, invite) are stages of a flow and stay on the URL they
  // started from. The screen is always set here too: going back to the page
  // already open ("Change email" from the code screen) changes no URL, so the
  // route would never set it. The last segment is replaced (/dev/signin →
  // /dev/signup), a trailing slash first dropped, and ?error= stays behind:
  // it was about the page it came with.
  const go = (s: AuthScreen) => {
    setError('');
    setScreen(s);
    if (!ENTRY.has(s) || !ENTRY.has(initial)) return;
    const here = pathname.replace(/\/+$/, '');
    const to = here.replace(/[^/]*$/, s);
    if (to === here) return;
    const query = new URLSearchParams(search);
    query.delete('error');
    const qs = query.toString();
    nav({ pathname: to, search: qs ? `?${qs}` : '' });
  };
  /** Signed in: a full load through the boot sequence, to `to`. */
  const land = (to: string, msg?: string) => {
    qc.clear();
    if (msg) sessionStorage.setItem('tr.toast', msg);
    window.location.assign(to);
  };
  /** Back where they were headed. */
  const finish = (msg?: string) => {
    // Parsed, not prefix-matched: `/\evil.com` starts with one slash and
    // still leaves the origin (auth/session.tsx explains).
    land(safeReturnPath(params.get('return')) ?? `${BASE}/`, msg);
  };
  /** A NEW account lands on its workspace's default collection (decided 2026-09-10). */
  const finishNew = async (msg: string) => {
    const id = await defaultCollection();
    land(id ? `${BASE}/collections/${encodeURIComponent(id)}` : `${BASE}/`, msg);
  };
  const run = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  const submit = () => {
    if (screen === 'signin') {
      if (!emailOk(email)) return setError('Enter a valid email address.');
      if (!pw) return setError('Enter your password.');
      return run(async () => { await signIn(email.trim(), pw); finish(); });
    }
    if (screen === 'signup') {
      if (!first.trim() || !last.trim()) return setError('Enter your first and last name.');
      if (!emailOk(email)) return setError('Enter a valid email address.');
      if (pw.length < 8) return setError('Password must be at least 8 characters.');
      return run(async () => {
        await signUp({ email: email.trim(), password: pw, givenName: first.trim(), familyName: last.trim() });
        setMode('signup');
        setCode(['', '', '', '', '', '']);
        go('verify');
      });
    }
    if (screen === 'forgot') {
      if (!emailOk(email)) return setError('Enter a valid email address.');
      return run(async () => { await requestReset(email.trim()); setMode('reset'); setCode(['', '', '', '', '', '']); go('verify'); });
    }
    if (screen === 'verify') {
      if (code.some((d) => !/^\d$/.test(d))) return setError('Enter the 6-digit code from the email.');
      if (mode === 'reset') { setPw(''); setPw2(''); return go('reset'); }
      return run(async () => { await verifyCode(email.trim(), code.join('')); go('setup'); });
    }
    if (screen === 'reset') {
      if (pw.length < 8) return setError('Password must be at least 8 characters.');
      if (pw !== pw2) return setError("Passwords don't match.");
      return run(async () => { await confirmResetCode(email.trim(), code.join(''), pw); finish('Password updated'); });
    }
    if (screen === 'setup') {
      return run(async () => {
        // Only the lines this screen asks for; the name stays as the account
        // has it (the server leaves it alone when neither half is sent).
        if (institution.trim() || ROLE_TITLE[role]) await updateProfile(undefined, undefined, { institution: institution.trim(), roleTitle: ROLE_TITLE[role] });
        await finishNew('Welcome to TileRiver');
      });
    }
    if (screen === 'invite') {
      if (!invite) return;
      if (!first.trim() || !last.trim()) return setError('Enter your first and last name.');
      if (pw.length < 8) return setError('Password must be at least 8 characters.');
      return run(async () => {
        const { workspace } = await signUp({ email: invite.email, password: pw, givenName: first.trim(), familyName: last.trim(), invite: inviteToken });
        // INTO THE WORKSPACE THEY WERE INVITED TO, not their own: their
        // personal one is still their default, and it is empty. Selected and
        // opened at Home, the same as choosing it in the switcher. Chosen
        // BEFORE signing in: the invitation is spent now, so if the sign-in
        // fails, "Sign in" is their way back and must land there too.
        if (workspace) useWorkspaceStore.getState().setSelected(workspace);
        await signIn(invite.email, pw);
        if (workspace) land(`${BASE}/`, `Welcome to ${invite.workspace}`);
        else await finishNew(`Welcome to ${invite.workspace}`);
      });
    }
  };
  const key = (e: React.KeyboardEvent) => { if (e.key === 'Enter') submit(); };
  const field = (lbl: string, el: React.ReactNode) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={label}>{lbl}</label>
      {el}
    </div>
  );

  return (
    <div className="tr-auth" style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', background: '#fff', color: '#0f0f10', fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
      <div className="tr-auth-art" style={{ position: 'relative', overflow: 'hidden', background: '#0f0f10', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '28px 32px' }}>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gridTemplateRows: 'repeat(4,1fr)', gap: 3, opacity: 0.9 }}>
          {TILES.map((bg, i) => <div key={i} style={{ background: `linear-gradient(135deg, ${bg}, ${TILES[(i + 5) % 16]})` }} />)}
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(15,15,16,.55) 0%, rgba(15,15,16,0) 35%, rgba(15,15,16,0) 60%, rgba(15,15,16,.75) 100%)' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => home(nav)}>
          <img src={`${BASE}/brand/logo-horizontal-light.svg`} alt="TileRiver" style={{ height: 28, width: 'auto', flex: 'none', display: 'block' }} />
        </div>
        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420, color: '#fff' }}>
          <span style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1.15, textWrap: 'pretty' }}>Digital pathology collections built for research collaboration and education.</span>
          <span style={{ fontSize: 14, lineHeight: 1.5, opacity: 0.85 }}>Organize multimodal studies into collections, collaborate at the pixel level, and publish cohorts for peer review, teaching, and discovery.</span>
        </div>
      </div>
      <div className="tr-auth-form" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 32px', overflow: 'auto' }}>
        <div style={{ width: '100%', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 22 }}>
          {/* Narrow screens drop the image panel (community.css), so the logo
              comes here instead, at the same fixed size. */}
          <div className="tr-auth-mobile-logo" style={{ alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => home(nav)}>
            <img src={`${BASE}/brand/logo-icon.svg`} alt="" style={{ height: 28, width: 'auto', flex: 'none', display: 'block' }} />
            <img src={`${BASE}/brand/logo-wordmark.svg`} alt="TileRiver" style={{ height: 20, width: 'auto', flex: 'none', display: 'block' }} />
          </div>
          {screen === 'signin' ? (
            <>
              <H title="Sign in" blurb="Welcome back." />
              <Google signup={false} offered={google} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {field('Email', <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={key} autoFocus autoComplete="email" style={input} />)}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <label style={label}>Password</label>
                    <a href="#" className="tr-hover-ink-underline" onClick={(e) => { e.preventDefault(); setMode('reset'); go('forgot'); }} style={{ fontSize: 12.5, color: '#6b6b70', textDecoration: 'none', whiteSpace: 'nowrap', flex: 'none' }}>Forgot password?</a>
                  </div>
                  <Password value={pw} onChange={setPw} onEnter={submit} autoComplete="current-password" />
                </div>
                <Err>{error}</Err>
                <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>Sign in</button>
              </div>
              {signupOffered ? (
                <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}>
                  New to TileRiver? <L onClick={() => { setPw(''); go('signup'); }}>Create a free account</L>
                </p>
              ) : null}
            </>
          ) : null}

          {screen === 'signup' ? (
            <>
              <H title="Create your free account" blurb="No card needed. 25 GB of upload space included." />
              <Google signup offered={google} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  {field('First name', <input value={first} onChange={(e) => setFirst(e.target.value)} onKeyDown={key} autoFocus autoComplete="given-name" style={input} />)}
                  {field('Last name', <input value={last} onChange={(e) => setLast(e.target.value)} onKeyDown={key} autoComplete="family-name" style={input} />)}
                </div>
                {field('Email', <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={key} autoComplete="email" style={input} />)}
                {field('Password', <Password value={pw} onChange={setPw} onEnter={submit} autoComplete="new-password" />)}
                <Err>{error}</Err>
                <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>Create account</button>
                <p style={{ margin: 0, fontSize: 12.5, color: '#8a8a8f', lineHeight: 1.5, textAlign: 'center' }}>
                  By continuing you agree to the <a {...legal('/terms')} style={{ color: '#6b6b70' }}>Terms</a> and <a {...legal('/privacy')} style={{ color: '#6b6b70' }}>Privacy policy</a>.
                </p>
              </div>
              <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}>
                Already have an account? <L onClick={() => { setPw(''); go('signin'); }}>Sign in</L>
              </p>
            </>
          ) : null}

          {screen === 'forgot' ? (
            <>
              <H title="Reset your password" blurb="Enter your email and we'll send you a code to choose a new one." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {field('Email', <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={key} autoFocus autoComplete="email" style={input} />)}
                <Err>{error}</Err>
                <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>Send code</button>
              </div>
              <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}><L onClick={() => go('signin')}>Back to sign in</L></p>
            </>
          ) : null}

          {screen === 'verify' ? (
            <>
              {/* Hedged on purpose: the server answers a taken address the same way it answers a new one, so this screen cannot promise a code went out. */}
              <H title="Check your email" blurb={<>{mode === 'reset' ? 'If that address has an account, we sent it' : 'If that address is new here, we sent it'} a 6-digit code: <span style={{ color: '#0f0f10', fontWeight: 500 }}>{email}</span>. Enter it below.</>} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Digits code={code} onChange={setCode} onEnter={submit} />
                <Err>{error}</Err>
                <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>{mode === 'reset' ? 'Continue' : 'Verify email'}</button>
              </div>
              <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}>
                Didn't get it?{' '}
                <L onClick={() => run(async () => { await resendCode(email.trim(), mode === 'reset' ? 'reset' : 'verify'); setCode(['', '', '', '', '', '']); toast(`If a code is due, a new one is on its way to ${email.trim()}`); })}>Resend code</L> ·{' '}
                <L onClick={() => go(mode === 'reset' ? 'forgot' : 'signup')}>Change email</L>
              </p>
            </>
          ) : null}

          {screen === 'reset' ? (
            <>
              <H title="Choose a new password" blurb={`For ${email}`} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {field('New password', <Password value={pw} onChange={setPw} onEnter={submit} autoComplete="new-password" autoFocus />)}
                {field('Confirm password', <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} onKeyDown={key} autoComplete="new-password" style={input} />)}
                <Err>{error}</Err>
                <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>Save and sign in</button>
              </div>
            </>
          ) : null}

          {screen === 'setup' ? (
            <>
              <H title="Tell us about you" blurb="This shows next to what you share. You can change it later." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {field('Institution', <input value={institution} onChange={(e) => setInstitution(e.target.value)} onKeyDown={key} autoFocus autoComplete="organization" style={input} />)}
                {field('Role', <Select value={role} options={ROLE_OPTIONS} onChange={setRole} width="100%" size="lg" />)}
                <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>Continue</button>
              </div>
              <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}><L onClick={() => void finishNew('Welcome to TileRiver')}>Skip for now</L></p>
            </>
          ) : null}

          {screen === 'invite' ? (
            invite ? (
              <>
                <H title={`Join ${invite.workspace}`} blurb="Tell us your name and choose a password. The invitation already confirmed your address." />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {field('First name', <input value={first} onChange={(e) => setFirst(e.target.value)} onKeyDown={key} autoFocus autoComplete="given-name" style={input} />)}
                    {field('Last name', <input value={last} onChange={(e) => setLast(e.target.value)} onKeyDown={key} autoComplete="family-name" style={input} />)}
                  </div>
                  {field('Email', <input type="email" value={invite.email} readOnly style={{ ...input, background: '#fafaf9', color: '#6b6b70' }} />)}
                  {field('Password', <Password value={pw} onChange={setPw} onEnter={submit} autoComplete="new-password" />)}
                  <Err>{error}</Err>
                  <button type="button" className="tr-hover-ink2" onClick={submit} disabled={busy} style={primary}>Join workspace</button>
                </div>
                <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}>
                  Already have an account? <L onClick={() => go('signin')}>Sign in</L>
                </p>
              </>
            ) : (
              <>
                <H title={error ? 'That invitation is no longer valid' : 'Checking your invitation…'} blurb={error ? 'It may have expired, been withdrawn, or already been used. Ask whoever invited you to send another.' : ''} />
                {error ? <p style={{ margin: 0, fontSize: 13.5, color: '#6b6b70', textAlign: 'center' }}><L onClick={() => go('signin')}>Go to sign in</L></p> : null}
              </>
            )
          ) : null}
        </div>
      </div>
    </div>
  );
}
